import { useEffect, useMemo, useState } from "react";
import { api, parseApiError, fmtCurrency } from "../lib/api";

function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
function addMonths(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function buildGrid(ym) {
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const startDow = first.getDay();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const WEEK = ["S", "M", "T", "W", "T", "F", "S"];

export default function AdminCalendar() {
  const today = new Date();
  const todayYm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [ym, setYm] = useState(todayYm);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeDay, setActiveDay] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/admin/calendar", { params: { month: ym } });
      setData(r.data);
    } catch (e) {
      setErr(parseApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ym]);

  const grid = useMemo(() => buildGrid(ym), [ym]);
  const days = data?.days || {};
  const todayIso = today.toISOString().slice(0, 10);
  const activeReservations = activeDay ? (days[activeDay]?.reservations || []) : [];

  return (
    <section data-testid="admin-calendar">
      <div className="admin-toolbar">
        <div className="admin-toolbar__title">Calendar — {monthLabel(ym)}</div>
        <div className="admin-toolbar__filters">
          <button data-testid="cal-prev" onClick={() => setYm(addMonths(ym, -1))} className="admin-btn admin-btn-ghost">‹ Prev</button>
          <button data-testid="cal-today" onClick={() => setYm(todayYm)} className="admin-btn admin-btn-ghost">Today</button>
          <button data-testid="cal-next" onClick={() => setYm(addMonths(ym, 1))} className="admin-btn admin-btn-ghost">Next ›</button>
        </div>
      </div>

      {err && <div className="admin-err" data-testid="admin-calendar-err">{err}</div>}
      {loading && <div className="admin-loading">Loading calendar…</div>}

      {!loading && !err && (
        <div className="admin-cal-wrap" data-testid="admin-cal-wrap">
          <div className="admin-cal">
            <div className="admin-cal-weekrow">
              {WEEK.map((d, i) => <div key={i} className="admin-cal-weekday">{d}</div>)}
            </div>
            <div className="admin-cal-grid">
              {grid.map((iso, idx) => {
                if (!iso) return <div key={idx} className="admin-cal-cell is-blank" />;
                const bucket = days[iso];
                const count = bucket?.reservations?.length || 0;
                const color = bucket?.statusColor; // 'gold' | 'green' | null
                const isToday = iso === todayIso;
                const isActive = activeDay === iso;
                const d = Number(iso.slice(8, 10));
                return (
                  <button
                    type="button"
                    key={idx}
                    className={`admin-cal-cell ${count ? `has-${color || "gold"}` : ""} ${isToday ? "is-today" : ""} ${isActive ? "is-active" : ""}`}
                    onClick={() => count && setActiveDay(iso)}
                    data-testid={`cal-day-${iso}`}
                    disabled={!count}
                  >
                    <span className="admin-cal-num">{d}</span>
                    {count > 0 && <span className="admin-cal-count">{count}</span>}
                  </button>
                );
              })}
            </div>
            <div className="admin-cal-legend">
              <span><i className="dot dot-gold" /> Held (deposit pending)</span>
              <span><i className="dot dot-green" /> Confirmed / paid</span>
              <span className="mute">·  Total this month: {data?.totalReservations ?? 0}</span>
            </div>
          </div>

          <aside className="admin-cal-panel" data-testid="admin-cal-panel">
            {!activeDay && <div className="mute">Click a day with reservations to see details.</div>}
            {activeDay && (
              <>
                <div className="admin-overline">{activeDay}</div>
                <h3 className="admin-cal-panel__title">{activeReservations.length} reservation{activeReservations.length === 1 ? "" : "s"}</h3>
                <ul className="admin-cal-list">
                  {activeReservations.map((r) => (
                    <li key={r.reservationID} data-testid={`cal-res-${r.reservationID}`}>
                      <div className="admin-cal-list__row">
                        <span className="mono">{r.confirmationCode}</span>
                        <span className={`pill ${r.reservationStatus === 1 ? "pill-green" : "pill-gold"}`}>{r.reservationStatusName || `Status ${r.reservationStatus}`}</span>
                      </div>
                      <div className="admin-cal-list__row mute small">
                        {[r.renterFirstName, r.renterLastName].filter(Boolean).join(" ")} — {r.renterEmail}
                      </div>
                      <div className="admin-cal-list__row mute small">
                        {(r.serviceDisplayName || r.assetName) || "Charter"} · {fmtCurrency(r.totalAmount, r.currencyCode || "USD")}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}
